import React, { useEffect, useRef, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { FONT_GSS_FUTURA } from '@monorepo/shared/src/themes/defaultTheme';
import { remCalc } from '@monorepo/shared/src/util/style-util';

type Props = {
  onChange: (e: string) => void;
  pageNumber: string;
  totalPages: number;
};

const PageNumberInput: React.FC<Props> = ({ onChange, pageNumber, totalPages }) => {
  const inputEl = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState(pageNumber);

  const classes = styles();

  const handleKeyPress = (e: KeyboardEvent) => {
    // Ignoring keydown during IME composition
    if ((e as any).isComposing || e.keyCode === 229) return;

    // Backspace/Delete keyCode
    if (e.keyCode === 8) return;

    // [0-9] keycode
    if (e.keyCode < 48 || e.keyCode > 57) e.preventDefault();
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isValidPageNumber(value)) {
      onChange(value);
    }
  };

  const isValidPageNumber = (page: string): boolean => {
    const input = parseInt(page);

    return !page || (Number.isInteger(input) && input >= 1 && input <= totalPages);
  };

  useEffect(() => {
    setInput(() => pageNumber);
  }, [pageNumber]);

  useEffect(() => {
    if (inputEl.current) inputEl.current.addEventListener('keypress', handleKeyPress);

    return () => {
      if (inputEl.current) inputEl.current.removeEventListener('keypress', handleKeyPress);
    };
  });

  return (
    <input
      className={classes.pageInputContainer}
      max={totalPages - 1}
      maxLength={totalPages.toString().length}
      min="1"
      onChange={handleOnChange}
      placeholder="44"
      ref={inputEl}
      value={input}
    />
  );
};

const styles = makeStyles((theme: any) => ({
  pageInputContainer: {
    backgroundColor: theme.palette.primary.contrastText,
    border: `2px solid ${theme.palette.black.main}`,
    borderRadius: '8px',
    color: theme.palette.grey[900],
    display: 'inline-block',
    fontFamily: FONT_GSS_FUTURA,
    height: remCalc(36),
    marginBottom: remCalc(15),
    marginTop: remCalc(5),
    width: remCalc(365),
    fontSize: '1rem',
    '&::placeholder': {
      color: '#757575',
      opacity: 1,
    },
  },
}));

export default PageNumberInput;
